/**
 * Store Footer Component
 *
 * A comprehensive footer with store branding, social links, and legal pages.
 * Supports both LTR and RTL layouts with dynamic menu items from database.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface StoreTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  fontFamily?: string;
}

interface FooterMenuItem {
  id: string;
  menu_section: 'column1' | 'column2' | 'column3' | 'legal';
  label: string;
  url?: string;
  page_id?: string;
  link_type: 'system_page' | 'custom_page' | 'external_url';
  store_page_type?: string;
  position: number;
  open_new_tab: boolean;
  is_visible: boolean;
}

interface FooterSettings {
  column1_title: string;
  column2_title: string;
  column3_title: string;
  show_column1: boolean;
  show_column2: boolean;
  show_column3: boolean;
  show_social_links: boolean;
  show_newsletter: boolean;
  newsletter_title: string;
  newsletter_description: string;
  copyright_text: string;
  footer_note?: string;
}

interface StoreFooterProps {
  store: {
    id: string;
    name: string;
    slug: string;
    description?: string;
    logo_url?: string;
    contact_email?: string;
    social_links?: Record<string, string>;
    theme?: StoreTheme;
  };
  direction?: 'ltr' | 'rtl';
}

// Default footer settings
const defaultSettings: FooterSettings = {
  column1_title: 'Quick Links',
  column2_title: 'Customer Service',
  column3_title: 'Information',
  show_column1: true,
  show_column2: true,
  show_column3: false,
  show_social_links: true,
  show_newsletter: false,
  newsletter_title: 'Subscribe to our newsletter',
  newsletter_description: 'Get the latest updates and offers',
  copyright_text: '© {year} {store_name}. All rights reserved.',
  footer_note: undefined
};

// Default menu items (fallback when no custom items configured)
const getDefaultMenuItems = (_storeSlug: string, t: ReturnType<typeof useTranslation<'store'>>['t']): FooterMenuItem[] => [
  {
    id: 'default-privacy',
    menu_section: 'legal',
    label: t('footer.privacyPolicy', 'Privacy Policy'),
    link_type: 'system_page',
    store_page_type: 'privacy',
    position: 0,
    open_new_tab: false,
    is_visible: true
  },
  {
    id: 'default-terms',
    menu_section: 'legal',
    label: t('footer.termsOfService', 'Terms of Service'),
    link_type: 'system_page',
    store_page_type: 'terms',
    position: 1,
    open_new_tab: false,
    is_visible: true
  },
  {
    id: 'default-cookies',
    menu_section: 'legal',
    label: t('footer.cookiePolicy', 'Cookie Policy'),
    link_type: 'system_page',
    store_page_type: 'cookies',
    position: 2,
    open_new_tab: false,
    is_visible: true
  },
  {
    id: 'default-refund',
    menu_section: 'legal',
    label: t('footer.refundPolicy', 'Refund Policy'),
    link_type: 'system_page',
    store_page_type: 'refund',
    position: 3,
    open_new_tab: false,
    is_visible: true
  }
];

export default function StoreFooter({ store, direction = 'ltr' }: StoreFooterProps) {
  const { t } = useTranslation('store');
  const theme = store.theme;
  const currentYear = new Date().getFullYear();

  const [settings, setSettings] = useState<FooterSettings>(defaultSettings);
  const [menuItems, setMenuItems] = useState<FooterMenuItem[]>([]);

  // Fetch footer settings and menu items
  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const response = await fetch(`/api/public/stores/${store.slug}/footer`);
        if (response.ok) {
          const data = await response.json() as { settings: FooterSettings; menuItems: FooterMenuItem[] };
          if (data.settings) {
            setSettings(data.settings);
          }
          if (data.menuItems && data.menuItems.length > 0) {
            setMenuItems(data.menuItems);
          } else {
            // Use default menu items if none configured
            setMenuItems(getDefaultMenuItems(store.slug, t));
          }
        } else {
          // Use defaults if API fails
          setMenuItems(getDefaultMenuItems(store.slug, t));
        }
      } catch {
        // Use defaults on error
        setMenuItems(getDefaultMenuItems(store.slug, t));
      }
    };

    fetchFooterData();
  }, [store.slug, t]);

  // Helper to get URL for a menu item
  const getMenuItemUrl = (item: FooterMenuItem): string => {
    if (item.link_type === 'external_url' && item.url) {
      return item.url;
    }
    if (item.link_type === 'system_page' && item.store_page_type) {
      return `/s/${store.slug}/${item.store_page_type}`;
    }
    if (item.link_type === 'custom_page' && item.page_id) {
      // For custom pages, we need to look up the slug - for now use page_id
      return `/s/${store.slug}/page/${item.page_id}`;
    }
    return '#';
  };

  // Filter and sort menu items by section
  const getItemsBySection = (section: FooterMenuItem['menu_section']) => {
    return menuItems
      .filter(item => item.menu_section === section && item.is_visible)
      .sort((a, b) => a.position - b.position);
  };

  // Format copyright text with placeholders
  const formatCopyrightText = (text: string): string => {
    return text
      .replace('{year}', currentYear.toString())
      .replace('{store_name}', store.name);
  };

  // Render a menu link
  const renderMenuLink = (item: FooterMenuItem) => {
    const url = getMenuItemUrl(item);
    const isExternal = item.link_type === 'external_url';

    if (isExternal) {
      return (
        <a
          key={item.id}
          href={url}
          target={item.open_new_tab ? '_blank' : undefined}
          rel={item.open_new_tab ? 'noopener noreferrer' : undefined}
          className="hover:text-gray-900 transition-colors"
        >
          {item.label}
        </a>
      );
    }

    return (
      <Link
        key={item.id}
        to={url}
        className="hover:text-gray-900 transition-colors"
      >
        {item.label}
      </Link>
    );
  };

  const column1Items = getItemsBySection('column1');
  const column2Items = getItemsBySection('column2');
  const column3Items = getItemsBySection('column3');
  const legalItems = getItemsBySection('legal');

  // Determine which columns to show
  const showColumn1 = settings.show_column1 && column1Items.length > 0;
  const showColumn2 = settings.show_column2 && column2Items.length > 0;
  const showColumn3 = settings.show_column3 && column3Items.length > 0;
  const showLegal = legalItems.length > 0;
  const showSocial = settings.show_social_links && store.social_links && Object.keys(store.social_links).length > 0;

  // Calculate grid columns based on what's visible
  const visibleColumns = [true, showColumn1, showColumn2, showColumn3, showLegal].filter(Boolean).length;
  const gridClass = visibleColumns <= 2
    ? 'grid-cols-1 md:grid-cols-2'
    : visibleColumns === 3
      ? 'grid-cols-1 md:grid-cols-3'
      : 'grid-cols-1 md:grid-cols-4';

  return (
    <footer className="border-t border-gray-200 bg-gray-50" dir={direction}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className={`grid ${gridClass} gap-8`}>
          {/* Store Info */}
          <div className={visibleColumns <= 3 ? 'col-span-1' : 'col-span-1 md:col-span-2'}>
            <div className="flex items-center gap-3 mb-4">
              {store.logo_url ? (
                <img src={store.logo_url} alt={store.name} className="h-10 w-auto" />
              ) : (
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                  style={{ backgroundColor: theme?.primaryColor || '#0d9488' }}
                >
                  {store.name.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="font-semibold text-lg text-gray-900">{store.name}</span>
            </div>
            {store.description && (
              <p className="text-gray-600 text-sm mb-4 max-w-md">
                {store.description}
              </p>
            )}

            {/* Social Links */}
            {showSocial && store.social_links && (
              <div className="flex items-center gap-4 mt-4">
                {store.social_links.facebook && (
                  <a
                    href={store.social_links.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Facebook"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                    </svg>
                  </a>
                )}
                {store.social_links.instagram && (
                  <a
                    href={store.social_links.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Instagram"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"/>
                    </svg>
                  </a>
                )}
                {store.social_links.twitter && (
                  <a
                    href={store.social_links.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Twitter"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"/>
                    </svg>
                  </a>
                )}
                {store.social_links.youtube && (
                  <a
                    href={store.social_links.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="YouTube"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                )}
                {store.social_links.tiktok && (
                  <a
                    href={store.social_links.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="TikTok"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                    </svg>
                  </a>
                )}
              </div>
            )}

            {/* Contact Info */}
            {store.contact_email && (
              <div className="mt-4">
                <a href={`mailto:${store.contact_email}`} className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                  {store.contact_email}
                </a>
              </div>
            )}
          </div>

          {/* Column 1 */}
          {showColumn1 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">{settings.column1_title}</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                {column1Items.map(item => (
                  <li key={item.id}>{renderMenuLink(item)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Column 2 */}
          {showColumn2 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">{settings.column2_title}</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                {column2Items.map(item => (
                  <li key={item.id}>{renderMenuLink(item)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Column 3 */}
          {showColumn3 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">{settings.column3_title}</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                {column3Items.map(item => (
                  <li key={item.id}>{renderMenuLink(item)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Legal Links */}
          {showLegal && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">{t('footer.legal', 'Legal')}</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                {legalItems.map(item => (
                  <li key={item.id}>{renderMenuLink(item)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              {formatCopyrightText(settings.copyright_text)}
            </p>
            {settings.footer_note && (
              <p className="text-sm text-gray-500">{settings.footer_note}</p>
            )}
            <div className="text-sm text-gray-500">
              {t('storefront.poweredBy', 'Powered by')}{' '}
              <a href="https://xtform.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">
                xtFORM
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
